import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth, API, useLocation2 } from "../App";
import { useUiVariant } from "../hooks/useUiVariant";
import {
  LayoutDashboard,
  Users,
  Calendar,
  Package,
  BarChart3,
  Settings,
  LogOut,
  Sparkles,
  Sun,
  X,
  Car,
  CalendarDays,
  FileText,
  Star,
} from "lucide-react";
export const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { locationForApi } = useLocation2();
  const ui = useUiVariant();

  // Low-stock badge count
  const [lowStockCount, setLowStockCount] = useState(0);
  useEffect(() => {
    const fetchLowStock = async () => {
      try {
        const res = await axios.get(`${API}/inventory/low-stock`, { withCredentials: true });
        const items = Array.isArray(res.data) ? res.data : [];
        const filtered = locationForApi
          ? items.filter(i => !i.location || i.location === locationForApi)
          : items;
        setLowStockCount(filtered.length);
      } catch { setLowStockCount(0); }
    };
    fetchLowStock();
    const interval = setInterval(fetchLowStock, 60000); // refresh every minute
    return () => clearInterval(interval);
  }, [locationForApi]);

  const navSections = [
    {
      title: "Főmenü",
      items: [
        { path: "/dashboard", label: "Főoldal", icon: LayoutDashboard },
        { path: "/calendar", label: "Naptár", icon: CalendarDays },
        { path: "/customers", label: "Ügyfelek", icon: Users },
        { path: "/workers", label: "Dolgozók", icon: Calendar },
      ],
    },
    {
      title: "Működés",
      items: [
        { path: "/services", label: "Szolgáltatások", icon: Sparkles },
        { path: "/inventory", label: "Készlet", icon: Package },
        { path: "/invoices", label: "Számlák", icon: FileText },
        { path: "/day-management", label: "Napnyitás/Zárás", icon: Sun },
      ],
    },
    {
      title: "Elemzés",
      items: [
        { path: "/statistics", label: "Statisztika", icon: BarChart3 },
        { path: "/reviews", label: "Értékelések", icon: Star },
        { path: "/settings", label: "Beállítások", icon: Settings },
      ],
    },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          app-sidebar fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 border-r border-slate-800
          transform transition-transform duration-300 ease-in-out flex flex-col
          ${isOpen ? "translate-x-0" : "-translate-x-full"}
          lg:translate-x-0
        `}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-3">
              {ui === "studio" ? (
                <>
                  <div className="studio-sidebar-brand-mark w-10 h-10 rounded-xl flex items-center justify-center">
                    <Car className="w-5 h-5 text-cyan-100" />
                  </div>
                  <div className="leading-tight">
                    <h1 className="text-[19px] font-extrabold tracking-tight text-white font-['Manrope']">
                      <span className="text-cyan-300">x</span>Clean
                    </h1>
                    <p className="text-[11px] uppercase tracking-wider text-slate-400">detailing app</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                    <Car className="w-6 h-6 text-slate-900" />
                  </div>
                  <div>
                    <h1 className="text-xl font-bold text-white font-['Manrope']">X-CLEAN</h1>
                    <p className="text-xs text-slate-500">Menedzsment</p>
                  </div>
                </>
              )}
            </Link>
            <button
              onClick={onClose}
              className="lg:hidden text-slate-400 hover:text-white"
              data-testid="close-sidebar-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Navigation - scrollable */}
        <nav className="flex-1 overflow-y-auto p-4 min-h-0">
          <div className="space-y-4">
            {navSections.map((section) => (
              <div key={section.title}>
                <p className="px-2 mb-1.5 text-[10px] tracking-widest uppercase text-slate-500">{section.title}</p>
                <ul className="space-y-1">
                  {section.items.map((item) => (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        onClick={onClose}
                        className={`
                          flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors
                          ${isActive(item.path)
                            ? "text-green-300 bg-gradient-to-r from-green-500/20 to-emerald-500/10 border border-green-500/25"
                            : "text-slate-400 hover:text-white hover:bg-white/5 border border-transparent"
                          }
                        `}
                        data-testid={`nav-${item.path.slice(1)}`}
                      >
                        <item.icon className="w-5 h-5 flex-shrink-0" />
                        <span className="font-medium flex-1">{item.label}</span>
                        {item.path === "/inventory" && lowStockCount > 0 && (
                          <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center leading-none">
                            {lowStockCount}
                          </span>
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </nav>

        {/* User Section - always visible at bottom */}
        <div className="p-4 border-t border-slate-800 mt-auto flex-shrink-0">
          <div className="flex items-center gap-3 mb-4">
            {user?.picture ? (
              <img
                src={user.picture}
                alt={user.name}
                className="w-10 h-10 rounded-full"
              />
            ) : (
              <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
                <span className="text-white font-medium">
                  {user?.name?.charAt(0) || "U"}
                </span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">
                {user?.role === "admin" ? "Admin" : "Dolgozó"}
              </p>
            </div>
          </div>
          <button
            onClick={logout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            <span>Kijelentkezés</span>
          </button>
        </div>
      </aside>
    </>
  );
};
